import { useState } from "react";
import { AnimatePresence } from "framer-motion";

import Button from "components/Button";
import usePhase from "utils/usePhase";

import styles from "./summary.module.css";
import Modal from "components/Modal";

import submitUnfinishedRequest from "utils/submitUnfinishedRequest";
import copyUnfinishedRequestUrl from "utils/copyUnfinishedRequestUrl";
import updateUnfinishedRequest from "utils/updateUnfinishedRequest";

interface ShareSummaryProps {
  data: Record<string, any>;
  diffData?: Record<string, any>;
  draftHash?: string | null;
  className?: string;
}

const ShareSummary = ({ data, diffData, draftHash, className }: ShareSummaryProps) => {
  const t = usePhase("summary");

  const [linkHasBeenCopied, setLinkHasBeenCopied] = useState(false);
  const [errorWithCopy, setErrorWithCopy] = useState(false);

  const classes = [styles.shareSummary];
  if (className) classes.push(className);
  if (!data.acceptedPrivacyPolicy) classes.push(styles.disabled);

  return (
    <>
      <div className={classes.join(" ")}>
        <div className={styles.shareSummaryRight}>
          {t("share_link_description")}
        </div>
        <div className={styles.shareSummaryLeft}>
          <Button
            icon="share-link"
            onClick={() => {
              const copyUrl = (hash: string) => {
                const url = `${window.location.origin}/yhteenveto/${hash}`;
                navigator.clipboard.writeText(url).then(() => {
                  setLinkHasBeenCopied(true);
                  setTimeout(() => setLinkHasBeenCopied(false), 3000);
                });
              };

              if (diffData) {
                updateUnfinishedRequest(diffData, (result) => {
                  if (result.success) copyUrl(result.hash);
                  else setErrorWithCopy(true);
                });
              } else if (draftHash) {
                copyUrl(draftHash);
              } else {
                copyUnfinishedRequestUrl(data, (result) => {
                  if (result.success) {
                    setLinkHasBeenCopied(true);
                    setTimeout(() => setLinkHasBeenCopied(false), 3000);
                  } else {
                    setErrorWithCopy(true);
                  }
                });
              }
            }}
            disabled={!data.acceptedPrivacyPolicy}
          >
            {t("copy_link_to_share")}
          </Button>
          <Button
            icon="whatsapp"
            onClick={() => {
              // ref: https://stackoverflow.com/a/39387533
              const windowReference = window.open();

              const handleResult = (result: any) => {
                if (result.success) {
                  const url = `${window.location.origin}/yhteenveto/${result.hash}`;
                  const waUrl = `https://wa.me/?text=${encodeURIComponent(
                    `${t("whatsapp_text_prefix")} ${url}`
                  )}`;
                  if (windowReference) windowReference.location = waUrl;
                } else {
                  setErrorWithCopy(true);
                }
              };

              if (diffData) {
                updateUnfinishedRequest(diffData, handleResult);
              } else if (draftHash) {
                handleResult({ success: true, hash: draftHash });
              } else {
                submitUnfinishedRequest(data, handleResult);
              }
            }}
            disabled={!data.acceptedPrivacyPolicy}
          >
            {t("share_on_whatsapp")}
          </Button>
        </div>
      </div>
      <AnimatePresence>
        {linkHasBeenCopied && (
          <Modal closeModal={() => setLinkHasBeenCopied(false)}>
            <span className="h2">{t("copied")}</span>
          </Modal>
        )}
        {errorWithCopy && (
          <Modal closeModal={() => setErrorWithCopy(false)}>
            <span className="h2">{t("error_with_copying_link")}</span>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShareSummary;
